import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

import { useLocalStorageState } from '../hooks/useLocalStorageState.js';
import type { LogEntry } from '../core/types.js';

/**
 * Backfill fields that were added to LogEntry in later sessions onto
 * entries persisted by earlier builds of the harness. Old entries (pre-
 * session-0007) lacked `tConnectedMs` / `tOnInternetMs` / `anomalies`;
 * accessing those fields on a raw localStorage value would crash. We
 * normalize on read so the rest of the UI can rely on the type contract.
 */
function normalizeEntry(entry: LogEntry): LogEntry {
  const e = entry as Partial<LogEntry>;
  return {
    ...entry,
    tConnectedMs: e.tConnectedMs ?? null,
    tOnInternetMs: e.tOnInternetMs ?? null,
    anomalies: e.anomalies ?? [],
  };
}

interface LogsContextValue {
  readonly entries: readonly LogEntry[];
  addEntry(entry: LogEntry): void;
  clear(): void;
}

const LOGS_KEY = 'soluu-harness:logs:v1';

const LogsContext = createContext<LogsContextValue | null>(null);

export function LogsProvider({ children }: { children: ReactNode }) {
  const [rawEntries, setEntries] = useLocalStorageState<readonly LogEntry[]>(LOGS_KEY, []);

  const entries = useMemo<readonly LogEntry[]>(
    () => rawEntries.map(normalizeEntry),
    [rawEntries],
  );

  const addEntry = useCallback(
    (entry: LogEntry) => setEntries((prev) => [...prev, entry]),
    [setEntries],
  );
  const clear = useCallback(() => setEntries([]), [setEntries]);

  const value = useMemo<LogsContextValue>(
    () => ({ entries, addEntry, clear }),
    [entries, addEntry, clear],
  );

  return <LogsContext.Provider value={value}>{children}</LogsContext.Provider>;
}

export function useLogs(): LogsContextValue {
  const ctx = useContext(LogsContext);
  if (!ctx) throw new Error('useLogs must be used inside <LogsProvider>');
  return ctx;
}
